import type { ElkNode } from 'elkjs/lib/elk-api';
import type { FlowGraph, FlowGraphNode } from '../api/contracts';

export const FLOW_CARD_WIDTH = 240;
export const FLOW_CARD_HEIGHT = 152;
export type FlowDirection = 'RIGHT' | 'DOWN';
/** Cómo se ve el dibujo entero: más ancho que alto, o más alto que ancho. */
export type FlowOrientation = 'horizontal' | 'vertical';
export interface FlowPoint {
  x: number;
  y: number;
}
export interface FlowLayout {
  positions: Map<string, FlowPoint>;
  routes: Map<string, FlowPoint[]>;
}
/** Un dibujo con el sentido en que avanzan sus capas (y apuntan sus flechas). */
export interface OrientedFlowLayout extends FlowLayout {
  direction: FlowDirection;
}
export type FlowLayouts = Readonly<Record<FlowOrientation, OrientedFlowLayout>>;

/** Ancho y alto del dibujo: la caja que encierra todas las tarjetas. */
export function flowLayoutSize(layout: FlowLayout): { width: number; height: number } {
  const positions = [...layout.positions.values()];
  if (positions.length === 0) {
    return { width: 0, height: 0 };
  }
  const xs = positions.map((point) => point.x);
  const ys = positions.map((point) => point.y);
  return {
    width: Math.max(...xs) + FLOW_CARD_WIDTH - Math.min(...xs),
    height: Math.max(...ys) + FLOW_CARD_HEIGHT - Math.min(...ys),
  };
}

/** Put edge counts halfway along the route, never on its destination card. */
export function routeMidpoint(points: FlowPoint[]): FlowPoint | undefined {
  let remaining =
    points.slice(1).reduce((length, point, index) => {
      const previous = points[index]!;
      return length + Math.hypot(point.x - previous.x, point.y - previous.y);
    }, 0) / 2;
  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1]!;
    const point = points[index]!;
    const length = Math.hypot(point.x - previous.x, point.y - previous.y);
    if (length > 0 && remaining <= length) {
      return {
        x: previous.x + ((point.x - previous.x) * remaining) / length,
        y: previous.y + ((point.y - previous.y) * remaining) / length,
      };
    }
    remaining -= length;
  }
  return points[0];
}

/** Stable tie breakers also cover undeclared steps and identical timestamps. */
export function compareFlowSteps(left: FlowGraphNode, right: FlowGraphNode): number {
  return (
    (left.order ?? Number.MAX_SAFE_INTEGER) - (right.order ?? Number.MAX_SAFE_INTEGER) ||
    (left.firstAt ?? '').localeCompare(right.firstAt ?? '') ||
    left.id.localeCompare(right.id)
  );
}

/** Status, counts and focus must never trigger a new layout during polling. */
export function flowTopologyKey(graph: FlowGraph): string {
  return JSON.stringify({
    nodes: [...graph.nodes].sort(compareFlowSteps).map((node) => node.id),
    edges: graph.edges
      .map(({ id, source, target }) => ({ id, source, target }))
      .sort((left, right) => left.id.localeCompare(right.id)),
  });
}

/**
 * Layered layout follows dependencies instead of wrapping every four steps.
 * ELK routes long edges around intermediate cards and handles branches, joins,
 * disconnected components and cycles. Fixed ports match the React Flow handles.
 * The engine is lazy loaded so the other monitor screens do not pay its cost.
 */
export async function layoutFlowGraph(
  topologyKey: string,
  direction: FlowDirection = 'RIGHT',
): Promise<FlowLayout> {
  const topology = JSON.parse(topologyKey) as {
    nodes: string[];
    edges: { id: string; source: string; target: string }[];
  };
  if (topology.nodes.length === 0) {
    return { positions: new Map(), routes: new Map() };
  }
  const { default: ELK } = await import('elkjs/lib/elk.bundled.js');
  const horizontal = direction === 'RIGHT';
  // Use internal ids: event ids may contain punctuation used by layout engines.
  const ids = new Map(topology.nodes.map((id, index) => [id, `n${index}`]));
  const input: ElkNode = {
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': direction,
      'elk.edgeRouting': 'ORTHOGONAL',
      'elk.spacing.nodeNode': '48',
      'elk.spacing.edgeNode': '24',
      'elk.layered.spacing.nodeNodeBetweenLayers': '88',
      'elk.layered.spacing.edgeNodeBetweenLayers': '24',
      'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
      'elk.randomSeed': '1',
      'elk.padding': '[top=24,left=24,bottom=24,right=24]',
    },
    children: topology.nodes.map((id) => {
      const internal = ids.get(id)!;
      return {
        id: internal,
        width: FLOW_CARD_WIDTH,
        height: FLOW_CARD_HEIGHT,
        layoutOptions: { 'elk.portConstraints': 'FIXED_POS' },
        ports: [
          {
            id: `${internal}:in`,
            x: horizontal ? 0 : FLOW_CARD_WIDTH / 2,
            y: horizontal ? FLOW_CARD_HEIGHT / 2 : 0,
            width: 0,
            height: 0,
            layoutOptions: { 'elk.port.side': horizontal ? 'WEST' : 'NORTH' },
          },
          {
            id: `${internal}:out`,
            x: horizontal ? FLOW_CARD_WIDTH : FLOW_CARD_WIDTH / 2,
            y: horizontal ? FLOW_CARD_HEIGHT / 2 : FLOW_CARD_HEIGHT,
            width: 0,
            height: 0,
            layoutOptions: { 'elk.port.side': horizontal ? 'EAST' : 'SOUTH' },
          },
        ],
      };
    }),
    edges: topology.edges.map((edge, index) => ({
      id: `e${index}`,
      sources: [`${ids.get(edge.source)}:out`],
      targets: [`${ids.get(edge.target)}:in`],
    })),
  };
  const result = await new ELK().layout(input);
  const positioned = new Map(result.children?.map((node) => [node.id, node]));
  return {
    positions: new Map(
      topology.nodes.map((id) => {
        const node = positioned.get(ids.get(id)!);
        return [id, { x: node?.x ?? 0, y: node?.y ?? 0 }];
      }),
    ),
    routes: new Map(
      (result.edges ?? []).map((edge) => {
        const section = edge.sections?.[0];
        return [
          topology.edges[Number(edge.id.slice(1))]!.id,
          section ? [section.startPoint, ...(section.bendPoints ?? []), section.endPoint] : [],
        ];
      }),
    ),
  };
}

/**
 * Los dos dibujos de una corrida, asignados por su FORMA: "horizontal" es el
 * apaisado y "vertical" el otro.
 *
 * POR QUE NO BASTA EL SENTIDO DE LAS FLECHAS: con capas de izquierda a derecha,
 * una cadena de pasos sale ancha, pero cuando un paso dispara muchos todos
 * quedan apilados en una misma columna y el dibujo sale alto. La carga de
 * catálogo (un paso que dispara siete) medía 1272×2400 px en "horizontal" y
 * 3456×920 px en "vertical": para quien mira, el botón estaba al revés.
 *
 * LA REGLA: horizontal avanza de izquierda a derecha, salvo que ese dibujo salga
 * más alto que ancho y el de arriba hacia abajo sea más apaisado; ahí se
 * intercambian. Un dibujo cuadrado o ya ancho conserva el sentido de siempre,
 * así las flechas no cambian de dirección por diferencias mínimas.
 */
export async function layoutFlowGraphByOrientation(topologyKey: string): Promise<FlowLayouts> {
  const [right, down] = await Promise.all([
    layoutFlowGraph(topologyKey, 'RIGHT'),
    layoutFlowGraph(topologyKey, 'DOWN'),
  ]);
  const byRight: OrientedFlowLayout = { ...right, direction: 'RIGHT' };
  const byDown: OrientedFlowLayout = { ...down, direction: 'DOWN' };
  const rightSize = flowLayoutSize(right);
  const downSize = flowLayoutSize(down);
  const rightIsTall = rightSize.height > rightSize.width;
  const downIsWider = downSize.width * rightSize.height > rightSize.width * downSize.height;
  return rightIsTall && downIsWider
    ? { horizontal: byDown, vertical: byRight }
    : { horizontal: byRight, vertical: byDown };
}
